'use client'

import { useMemo } from 'react'
import { StatsGrid } from '../common/StatsGrid'
import { TodoList } from '../common/TodoList'
import { SectionHeader } from '../common/SectionHeader'
import { EmptyState } from '../common/EmptyState'
import { CompanyProjectsTabs } from './CompanyProjectsTabs'
import { RecentQuotes } from './RecentQuotes'
import { InboxPreview } from './InboxPreview'
import { RecommendedTeams } from './RecommendedTeams'
import type { CompanyDashboardResponse } from '@/types/dashboard'
import { BriefcaseBusiness, FileText, AlertCircle, Clock, Bell } from 'lucide-react'

interface CompanyDashboardProps {
  data: CompanyDashboardResponse
  userName?: string | null
}

export function CompanyDashboard({ data, userName }: CompanyDashboardProps) {
  const { stats, todo, sections } = data

  const statCards = useMemo(
    () => [
      {
        icon: BriefcaseBusiness,
        label: '진행중 프로젝트',
        value: stats.activeProjects,
        href: '/client/projects?tab=active',
        miniCtaLabel: '진행 목록',
      },
      {
        icon: FileText,
        label: '견적 도착',
        value: stats.unreadQuotes,
        href: '/client/quotes?filter=unread',
        highlight: stats.unreadQuotes > 0,
        subLabel: '최근 24시간 기준',
        miniCtaLabel: '비교하기',
      },
      {
        icon: AlertCircle,
        label: '결정 필요',
        value: stats.needsDecisionProjects,
        href: '/client/projects?tab=review',
        highlight: stats.needsDecisionProjects > 0,
        zeroLabel: '결정 대기 없음',
        miniCtaLabel: '결정 필요 목록',
      },
      {
        icon: Clock,
        label: '마감 임박',
        value: stats.dueSoonProjects,
        href: '/client/projects?sort=due_soon',
        highlight: stats.dueSoonProjects > 0,
        zeroLabel: '마감 임박 없음',
        miniCtaLabel: '마감 임박 보기',
      },
      {
        icon: Bell,
        label: '읽지 않은 쪽지',
        value: stats.unreadInbox,
        href: '/client/inbox',
        highlight: stats.unreadInbox > 0,
        miniCtaLabel: '확인하기',
      },
    ],
    [
      stats.activeProjects,
      stats.unreadQuotes,
      stats.needsDecisionProjects,
      stats.dueSoonProjects,
      stats.unreadInbox,
    ]
  )

  const isEmpty = useMemo(() => {
    const noProjects =
      sections.projects.requesting.length === 0 &&
      sections.projects.reviewing.length === 0 &&
      sections.projects.active.length === 0

    const noSignals =
      stats.activeProjects === 0 &&
      stats.unreadQuotes === 0 &&
      stats.needsDecisionProjects === 0 &&
      stats.dueSoonProjects === 0 &&
      stats.unreadInbox === 0

    return noProjects && noSignals
  }, [
    stats.activeProjects,
    stats.unreadQuotes,
    stats.needsDecisionProjects,
    stats.dueSoonProjects,
    stats.unreadInbox,
    sections.projects.requesting.length,
    sections.projects.reviewing.length,
    sections.projects.active.length,
  ])

  return (
    <div className="space-y-10 pb-10">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">
          안녕하세요, <span className="text-blue-600">{userName || '기업 담당자'}</span>님 👋
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          오늘은 프로젝트 의뢰와 견적 검토에 집중해볼까요?
        </p>
        <div className="flex flex-wrap gap-2">
          <a href="/client/projects/new" className="inline-flex h-10 items-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white">프로젝트 등록</a>
          <a href="/client/quotes" className="inline-flex h-10 items-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700">견적 비교/관리로 이동</a>
        </div>
      </div>

      {isEmpty ? (
        <EmptyState
          icon={BriefcaseBusiness}
          title="아직 프로젝트가 없습니다"
          description="첫 프로젝트를 등록하고 검증된 팀을 찾아보세요."
          cta={{
            label: '프로젝트 등록하기',
            href: '/client/projects/new',
          }}
        />
      ) : (
        <>
          {/* KPI */}
          <section className="space-y-3">
            <SectionHeader title="요약" />
            {/* StatsGrid가 columns만 받는다면, 내부에서 반응형 처리하도록 추천.
                일단 여기선 모바일에서 덜 답답하게 보이도록 wrapper로 대응 */}
            <div className="w-full">
              <StatsGrid stats={statCards} columns={5} />
            </div>
          </section>

          {/* Todo */}
          <section className="space-y-3">
            <SectionHeader title="오늘의 할 일" />
            <p className="text-sm text-gray-500">처리 순서대로 정리해뒀어요</p>
            <TodoList todos={todo} emptyCtaHref="/client/projects/new" emptyCtaLabel="프로젝트 등록" />
          </section>

          {/* Projects */}
          <section className="space-y-3">
            <SectionHeader title="내 프로젝트" viewAllHref="/client/projects" />
            <CompanyProjectsTabs projects={sections.projects} />
          </section>

          {/* Recent Quotes */}
          <section className="space-y-3">
            <SectionHeader title="최근 견적" viewAllHref="/client/quotes" />
            {sections.recentQuotes.length > 0 ? (
              <RecentQuotes quotes={sections.recentQuotes} />
            ) : (
              <EmptyState
                icon={FileText}
                title="도착한 견적이 없습니다"
                description="프로젝트를 등록하고 팀에게 견적을 요청해보세요."
                cta={{ label: '프로젝트 등록하기', href: '/client/projects/new' }}
              />
            )}
          </section>

          {/* Inbox Preview */}
          <section className="space-y-3">
            <SectionHeader title="Inbox 미리보기" viewAllHref="/client/inbox" />
            {sections.inboxPreview.length > 0 ? (
              <InboxPreview items={sections.inboxPreview} />
            ) : (
              <EmptyState
                icon={Bell}
                title="새 쪽지가 없습니다"
                description="팀과의 메시지가 이곳에 표시됩니다."
                cta={{ label: '쪽지함 열기', href: '/client/inbox' }}
              />
            )}
          </section>

          {/* Recommended Teams */}
          {sections.recommendedTeams && (
            <section className="space-y-3">
              <SectionHeader title="추천 팀" viewAllHref="/client/teams" />
              {sections.recommendedTeams.length > 0 ? (
                <RecommendedTeams teams={sections.recommendedTeams} />
              ) : (
                <EmptyState
                  icon={BriefcaseBusiness}
                  title="추천 팀이 아직 없습니다"
                  description="추천을 받으려면 프로젝트에 3가지를 채워주세요: 예산, 기간, 필요 스킬"
                  cta={{ label: '프로젝트 정보 보완하기', href: '/client/projects' }}
                />
              )}
            </section>
          )}
        </>
      )}
    </div>
  )
}
